import moment from "moment";
import Button from "pages/_components/Button";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { push } from "react-router-redux";
import { compose } from "redux";

import { actions, selectors } from "reducers/checks";
import {
    echeqShape,
    getAccountMessage,
    getPendingEcheqSignatureTransactionLocation,
    PENDING_ECHEQ_SIGNATURES_NOTIFICATION_SCOPES,
    validateEcheqAccounts,
} from "util/checks";
import * as configUtils from "util/config";
import * as i18n from "util/i18n";

import DrawerModal from "pages/_components/drawer/DrawerModal";
import CustodyEcheq from "pages/checks/Echeqs/EcheqActions/CustodyEcheq";
import DepositEcheq from "pages/checks/Echeqs/EcheqActions/DepositEcheq";
import RepudiateEcheq from "pages/checks/Echeqs/EcheqActions/RepudiateEcheq";
import { actions as notificationActions } from "reducers/notification";
import { selectors as accountsSelectors } from "reducers/accounts";
import { selectors as sessionSelectors } from "reducers/session";

class MultipleEcheqsButtons extends Component {
    static propTypes = {
        environmentCuit: string.isRequired,
        selectedRows: arrayOf(echeqShape).isRequired,
        dispatch: func.isRequired,
        isDesktop: bool.isRequired,
        echeqsListTotal: number.isRequired,
        currency: string.isRequired,
        accounts: arrayOf(shape({})).isRequired,
        isRetail: bool.isRequired,
        serverSearchDate: string.isRequired,
        location: shape({
            pathname: string.isRequired,
        }).isRequired,
    };

    state = {
        showDrawer: false,
        contentDrawer: "",
    };

    /**
     * Algún cheque seleccionado tiene transacción PENDING de firma para la misma actividad masiva
     * (no mezclar con nuevo envío).
     */
    selectionHasPendingSignatureForBulkAction = (uiAction) => {
        if (!configUtils.getBoolean("frontend.show.pendingECHEQs.functionalities", false)) {
            return false;
        }
        const { selectedRows } = this.props;
        return selectedRows.some((echeq) => getPendingEcheqSignatureTransactionLocation(echeq, uiAction) != null);
    };

    receivedEcheqAction = (action) => {
        const {
            selectedRows,
            dispatch,
            isDesktop,
            echeqsListTotal,
            currency,
            accounts,
            isRetail,
            location,
        } = this.props;
        if (selectedRows.length === 1) {
            const pendingLoc = getPendingEcheqSignatureTransactionLocation(selectedRows[0], action);
            if (pendingLoc) {
                dispatch(push(pendingLoc));
                dispatch(
                    notificationActions.showNotification(
                        i18n.get("echeqs.pending.signatures.signingSchemeRequired"),
                        "warning",
                        PENDING_ECHEQ_SIGNATURES_NOTIFICATION_SCOPES,
                        null,
                        false,
                    ),
                );
                return;
            }
        }
        if (
            selectedRows.length > 1 &&
            ["ENDORSE", "EMIT-CESION", "NEGOTIATE"].includes(action) &&
            this.selectionHasPendingSignatureForBulkAction(action)
        ) {
            dispatch(
                notificationActions.showNotification(
                    i18n.get("echeqs.pending.signatures.signingSchemeRequired"),
                    "warning",
                    PENDING_ECHEQ_SIGNATURES_NOTIFICATION_SCOPES,
                    null,
                    false,
                ),
            );
            return;
        }
        const echeqList = selectedRows;

        dispatch(
            actions.setEcheqsToDoActions({
                action,
                echeqList,
                echeqsListTotal,
                ...(location.pathname ? { confirmGoBackPath: location.pathname } : {}),
            }),
        );

        switch (action) {
            case "ADMIT": {
                dispatch(push("/echeqs/action/confirmation"));
                break;
            }
            case "REPUDIATE": {
                this.setState({
                    showDrawer: true,
                    contentDrawer: <RepudiateEcheq isDesktop={isDesktop} />,
                    title: !isDesktop && "echeqs.action.motive.title",
                });
                break;
            }
            case "DEPOSIT": {
                if (
                    configUtils.getBoolean("frontend.show.echeqsActions.text.functionalities") &&
                    validateEcheqAccounts(accounts, false, currency)
                ) {
                    const message = getAccountMessage(currency, isRetail);
                    dispatch(
                        notificationActions.showNotification(configUtils.get(message), "warning", ["echeqsMainPage"]),
                    );
                } else {
                    this.setState({
                        showDrawer: true,
                        contentDrawer: <DepositEcheq currency={currency} isDesktop={isDesktop} />,
                        title: !isDesktop && "echeqs.action.deposit.title",
                    });
                }
                break;
            }
            case "CUSTODY": {
                if (
                    configUtils.getBoolean("frontend.show.echeqsActions.text.functionalities") &&
                    validateEcheqAccounts(accounts, false, currency)
                ) {
                    const message = getAccountMessage(currency, isRetail);
                    dispatch(
                        notificationActions.showNotification(configUtils.get(message), "warning", ["echeqsMainPage"]),
                    );
                } else {
                    this.setState({
                        showDrawer: true,
                        contentDrawer: <CustodyEcheq currency={currency} isDesktop={isDesktop} />,
                        title: !isDesktop && "echeqs.action.custody.title",
                    });
                }
                break;
            }
            case "RESCUE": {
                dispatch(push("/echeqs/action/confirmation"));
                break;
            }
            case "ENDORSE": {
                dispatch(push("/echeqs/action/endorse"));
                break;
            }
            case "EMIT-CESION": {
                dispatch(push("/echeqs/action/cesion"));
                break;
            }
            case "NEGOTIATE": {
                dispatch(push({ pathname: "/echeqs/action/negotiate", state: { currency } }));
                break;
            }

            default:
                break;
        }
    };

    checkEnableAdmit = (button) => {
        const { environmentCuit, selectedRows, serverSearchDate } = this.props;
        const echeqsActivoPendienteAutomationEnabled = configUtils.getBoolean(
            "echeq.config.automation.activo.pendiente.enabled",
        );
        const echeqsCesionPendienteAutomationEnabled = configUtils.getBoolean(
            "echeq.config.automation.cesion.pendiente.enabled",
        );
        const echeqsEmitidoPendienteAutomationEnabled = configUtils.getBoolean(
            "echeq.config.automation.emitido.pendiente.enabled",
        );
        const echeqsActivoPendienteAutomationDiscountEnabled = configUtils.getBoolean(
            "echeq.config.automationDiscount.activo.pendiente.enabled",
        );
        const echeqsCesionPendienteAutomationDiscountEnabled = configUtils.getBoolean(
            "echeq.config.automationDiscount.cesion.pendiente.enabled",
        );
        const echeqsEmitidoPendienteAutomationDiscountEnabled = configUtils.getBoolean(
            "echeq.config.automationDiscount.emitido.pendiente.enabled",
        );
        const echeqsCustodyAutomationDiscountEnabled = configUtils.getBoolean(
            "echeq.config.automationDiscount.custody.enabled",
        );

        const echeqsRescueActionEnabled = configUtils.getBoolean("frontend.echeqs.massRescue.functionalities");

        let disable = !selectedRows.length > 0;
        if (button === "ADMIT" || button === "REPUDIATE") {
            selectedRows.forEach((echeq) => {
                if (
                    (echeq.estado === "ACTIVO-PENDIENTE" &&
                        (echeq.total_endosos === 0 || echeq.tenencia_beneficiario_documento !== environmentCuit)) ||
                    echeq.estado === "ACTIVO" ||
                    echeq.estado === "CUSTODIA"
                ) {
                    disable = true;
                }
            });
        }
        switch (button) {
            case "CUSTODY": {
                selectedRows.forEach((echeq) => {
                    const validDateForCustody = moment(echeq.fecha_pago) > moment();
                    if (validDateForCustody) {
                        if (
                            echeq.estado !== "ACTIVO" &&
                            !(echeq.estado === "ACTIVO-PENDIENTE" && echeqsActivoPendienteAutomationEnabled) &&
                            !(echeq.estado === "EMITIDO-PENDIENTE" && echeqsEmitidoPendienteAutomationEnabled) &&
                            !(echeq.estado === "CESION-PENDIENTE" && echeqsCesionPendienteAutomationEnabled)
                        ) {
                            disable = true;
                        }
                    } else {
                        disable = true;
                    }
                });
                break;
            }
            case "DEPOSIT": {
                selectedRows.forEach((echeq) => {
                    const fechaPago = moment(echeq.fecha_pago)
                        .utc()
                        .format("YYYY-MM-DD");
                    const fechaHoy = moment()
                        .utc()
                        .format("YYYY-MM-DD");
                    const diff = Math.abs(moment(fechaPago).diff(moment(fechaHoy), "days"));
                    const validDateForDeposit = (fechaPago < fechaHoy && diff <= 30) || diff === 0;
                    if (validDateForDeposit) {
                        if (
                            echeq.estado !== "ACTIVO" &&
                            !(echeq.estado === "ACTIVO-PENDIENTE" && echeqsActivoPendienteAutomationEnabled) &&
                            !(echeq.estado === "EMITIDO-PENDIENTE" && echeqsEmitidoPendienteAutomationEnabled) &&
                            !(echeq.estado === "CESION-PENDIENTE" && echeqsCesionPendienteAutomationEnabled)
                        ) {
                            disable = true;
                        }
                    } else {
                        disable = true;
                    }
                });
                break;
            }
            case "ENDORSE": {
                if (this.selectionHasPendingSignatureForBulkAction("ENDORSE")) {
                    disable = true;
                }
                selectedRows.forEach((echeq) => {
                    if (echeq.estado !== "ACTIVO" || echeq.cheque_caracter.toLowerCase() === "no a la orden") {
                        disable = true;
                    }
                });
                break;
            }
            case "CESION": {
                if (this.selectionHasPendingSignatureForBulkAction("EMIT-CESION")) {
                    disable = true;
                }
                selectedRows.forEach((echeq) => {
                    if (echeq.estado !== "ACTIVO" || echeq.cheque_caracter.toLowerCase() !== "no a la orden") {
                        disable = true;
                    }
                });
                break;
            }
            case "NEGOTIATE": {
                if (this.selectionHasPendingSignatureForBulkAction("NEGOTIATE")) {
                    disable = true;
                }
                selectedRows.forEach((echeq) => {
                    if (
                        echeq.estado !== "ACTIVO" &&
                        !(echeq.estado === "ACTIVO-PENDIENTE" && echeqsActivoPendienteAutomationDiscountEnabled) &&
                        !(echeq.estado === "EMITIDO-PENDIENTE" && echeqsEmitidoPendienteAutomationDiscountEnabled) &&
                        !(echeq.estado === "CESION-PENDIENTE" && echeqsCesionPendienteAutomationDiscountEnabled) &&
                        !(echeq.estado === "CUSTODIA" && echeqsCustodyAutomationDiscountEnabled)
                    ) {
                        disable = true;
                    }
                });
                break;
            }
            case "RESCUE": {
                const searchDate = serverSearchDate ? moment(serverSearchDate) : moment();

                selectedRows.forEach((echeq) => {
                    const fechaPago = moment(echeq.fecha_pago);
                    const diffDays = moment.duration(searchDate.diff(fechaPago)).asDays();
                    if (
                        !(
                            echeqsRescueActionEnabled &&
                            echeq.estado === "CUSTODIA" &&
                            echeq.tenencia_beneficiario_documento === environmentCuit &&
                            diffDays < 0
                        )
                    ) {
                        disable = true;
                    }
                });

                break;
            }
            default: {
                break;
            }
        }

        return disable;
    };

    render() {
        const { isDesktop, echeqsListTotal } = this.props;
        const { showDrawer, contentDrawer, title } = this.state;
        const echeqsRescueActionEnabled = configUtils.getBoolean("frontend.echeqs.massRescue.functionalities");
        return (
            <>
                {showDrawer && (
                    <DrawerModal
                        content={contentDrawer}
                        width={isDesktop ? "30%" : "100%"}
                        isDesktop={isDesktop}
                        title={title}
                        showDrawerRemote={showDrawer}
                        closeRemotely={() => this.setState({ showDrawer: false, contentDrawer: "" })}
                        drawerClassName="echeq__drawer"
                    />
                )}
                {echeqsListTotal > 0 && (
                    <div className="w-100 d-flex flex-flow-wrap">
                        <Button
                            bsStyle="primary"
                            className="echeq__action-button"
                            label="echeq.admit.echeq"
                            type="button"
                            disabled={this.checkEnableAdmit("ADMIT")}
                            onClick={() => this.receivedEcheqAction("ADMIT")}
                        />
                        <Button
                            bsStyle="primary"
                            className="echeq__action-button"
                            label="echeq.repudiate.echeq"
                            type="button"
                            disabled={this.checkEnableAdmit("REPUDIATE")}
                            onClick={() => this.receivedEcheqAction("REPUDIATE")}
                        />
                        <Button
                            bsStyle="primary"
                            className="echeq__action-button"
                            label="echeq.deposit.echeq"
                            type="button"
                            disabled={this.checkEnableAdmit("DEPOSIT")}
                            onClick={() => this.receivedEcheqAction("DEPOSIT")}
                        />
                        <Button
                            bsStyle="primary"
                            className="echeq__action-button"
                            label="echeq.custody.echeq"
                            type="button"
                            disabled={this.checkEnableAdmit("CUSTODY")}
                            onClick={() => this.receivedEcheqAction("CUSTODY")}
                        />
                        {echeqsRescueActionEnabled && (
                            <Button
                                bsStyle="primary"
                                className="echeq__action-button"
                                label="echeq.rescue.echeq"
                                type="button"
                                disabled={this.checkEnableAdmit("RESCUE")}
                                onClick={() => this.receivedEcheqAction("RESCUE")}
                            />
                        )}
                        <Button
                            bsStyle="primary"
                            className="echeq__action-button"
                            label="echeqs.button.endorse"
                            type="button"
                            disabled={this.checkEnableAdmit("ENDORSE")}
                            onClick={() => this.receivedEcheqAction("ENDORSE")}
                        />
                        <Button
                            bsStyle="primary"
                            className="echeq__action-button"
                            label="echeq.emit.cesion.echeq"
                            type="button"
                            disabled={this.checkEnableAdmit("CESION")}
                            onClick={() => this.receivedEcheqAction("EMIT-CESION")}
                        />

                        <Button
                            bsStyle="primary"
                            className="echeq__action-button"
                            label="echeqs.button.discount"
                            type="button"
                            disabled={this.checkEnableAdmit("NEGOTIATE")}
                            onClick={() => this.receivedEcheqAction("NEGOTIATE")}
                        />
                    </div>
                )}
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    option: selectors.getEcheqOption(state),
    echeqsList: selectors.getEcheqsList(state),
    environmentCuit: selectors.getEnvironmentCuit(state),
    accounts: accountsSelectors.getAccounts(state),
    isRetail: sessionSelectors.isRetailEnvironment(state),
    serverSearchDate: selectors.getServerSearchDate(state),
});

export default compose(withRouter, connect(mapStateToProps))(MultipleEcheqsButtons);
